/**
 * Phone number helpers — display formatting, `tel:` hrefs and search
 * normalisation. French numbers render in the canonical
 * "+33 6 12 34 56 78" grouping so a contact looks the same whether the
 * backend stored it as "0612345678", "+33612345678" or "06.12.34.56.78".
 */

/** Strip a raw phone string down to bare digits — drops "+", spaces,
 *  dots, dashes and parentheses. Use it to compare or search numbers
 *  regardless of how they were typed or stored. */
export function normalizePhone(input: string | null | undefined): string {
  return (input ?? "").replace(/\D/g, "");
}

/** A value that can be dialled: at least six digits. Placeholders an old
 *  import left in the phone field ("1", ".", "..") are not numbers: they
 *  are never shown, linked or copied as if they were. */
export function isPhoneNumber(raw: string | null | undefined): raw is string {
  return normalizePhone(raw).length >= 6;
}

/** The contact's number was copied from its company's number (repair of
 *  the legacy M&A placeholders, owner decision 2026-10-30): the UI says
 *  so next to it. */
export function phoneCopiedFromCompany(
  contact:
    | { metadata?: Record<string, unknown>; extra_metadata?: Record<string, unknown> }
    | null
    | undefined,
): boolean {
  const meta = contact?.metadata ?? contact?.extra_metadata;
  const correction = meta?.phone_correction as { action?: unknown } | undefined;
  return correction?.action === "copied_company_mobile";
}

/** What the UI writes beside a number copied from the company's. */
export const COPIED_FROM_COMPANY_LABEL = "recopié du numéro société";

/** Format a raw phone number for display. French numbers (national
 *  `0X…` or international `+33…` / `0033…`) collapse to the canonical
 *  "+33 6 12 34 56 78"; anything else falls back to a generic
 *  two-digit grouping. Returns "—" for empty values and for values that
 *  are not numbers, so callers can drop it straight into a table cell. */
export function formatPhone(raw: string | null | undefined): string {
  if (!isPhoneNumber(raw)) return "—";
  let digits = normalizePhone(raw);
  // Drop an international call prefix ("0033…" → "33…").
  if (digits.startsWith("00")) digits = digits.slice(2);
  // National French form ("0X XX…", 10 digits) → international.
  if (digits.length === 10 && digits.startsWith("0")) {
    digits = `33${digits.slice(1)}`;
  }
  // Canonical French number — country code 33 + 9 subscriber digits.
  if (digits.startsWith("33") && digits.length === 11) {
    const d = digits.slice(2);
    return `+33 ${d[0]} ${d.slice(1, 3)} ${d.slice(3, 5)} ${d.slice(5, 7)} ${d.slice(7, 9)}`;
  }
  // Fallback — group by two digits.
  return digits.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
}

/** Build a `tel:`-safe href from a raw phone string — keeps a leading
 *  "+" and digits, drops every separator. Returns null for empty
 *  input so callers can conditionally render the link. */
export function phoneHref(raw: string | null | undefined): string | null {
  if (!isPhoneNumber(raw)) return null;
  const cleaned = raw.replace(/[^\d+]/g, "");
  return cleaned ? `tel:${cleaned}` : null;
}

/** A number as the copy buttons copy it: a French number as it is shown
 *  ("+33 6 12 34 56 78", like the Call Center's Copy), any other as it is
 *  stored, since the display's fallback grouping drops its "+". */
export function phoneForCopy(raw: string): string {
  const shown = formatPhone(raw);
  return shown.startsWith("+33 ") ? shown : raw.trim();
}
